import isEmpty from 'lodash/isEmpty'
import Cookies from 'js-cookie'

import { getAdtechFeatureFlag } from '@/api/ads'
import MerchantUserApi from '@/api/merchant/users'
import MerchantProfileApi from '@/api/merchant/profile'
import router from '@/router'
import store from '@/store/index'
import locator from '@/service-locator'
import {
  getFinishCollectPhoneNumberFromCache,
  cacheFinishedCollectPhoneNumber,
} from '@/utils/collect-phone-number'
import { getDeviceType, getOperatingSystem } from '@/utils/device-type'
import RequestCacheInstance from '@/utils/request-cache'
import { STORAGE_KEYS } from '@/constants/storageKeys'
import { clearStorageKeys } from '@/utils/local-storage'

const state = {
  data: null,
  selectedMerchant: '',
  roles: [],
  permissions: [],
  fetching: false,
  onboardingId: null,
  error: null,
  timezone: 'Asia/Riyadh',
  collectPhoneNumberDialogVisible: false,
  finishedCollectPhoneNumber: getFinishCollectPhoneNumberFromCache() === 'true',
}

const getters = {
  profile: (state) => {
    return state.data || {}
  },
  subMerchants: (_, getters) => {
    return getters.profile.stores || []
  },
  selectedMerchant: (state) => {
    return state.selectedMerchant
  },
  userPermissions: (state) => {
    return state.permissions
  },
  timezone: (state) => {
    return state.timezone
  },
  userRole: (state) => {
    return state.data?.role || ''
  },
  collectPhoneNumberDialogVisible: (state) => {
    return (
      state.collectPhoneNumberDialogVisible && !state.finishedCollectPhoneNumber
    )
  },
}

const mutations = {
  SET_STATUS: (state, { key, value }) => {
    state[key] = value
  },
  SET_SELECTED_MERCHANT: (state, merchantId) => {
    state.selectedMerchant = merchantId
    localStorage.setItem('selectedMerchant', merchantId)
    Cookies.set('selectedMerchant', merchantId)
  },
  SET_MERCHANT_USER_DATA: (state, data) => {
    state.data = data
    state.roles = data.roles || []
    state.permissions = data.permissions || []
  },
  SET_MERCHANT_ERROR: (state, error) => {
    state.error = error
  },
  SET_ONBOARDING_ID: (state, onboardingId) => {
    state.onboardingId = onboardingId
  },
  CLEAR_MERCHANT_USER_DATA: (state) => {
    state.data = null
    state.selectedMerchant = ''

    // Only clear essential merchant-specific and temporary data
    const essentialKeysToClear = [
      STORAGE_KEYS.selectedMerchant,
      STORAGE_KEYS.adTechMerchant,
      STORAGE_KEYS.firedEvents,
      STORAGE_KEYS.adsCampaignCreateSessionId,
      STORAGE_KEYS.adsCampaignEditSessionId,
      STORAGE_KEYS.adsTopupSessionId,
      STORAGE_KEYS.onboardingSession,
      STORAGE_KEYS.onboardingSessionTimeout,
    ]

    clearStorageKeys(essentialKeysToClear)

    if (RequestCacheInstance?.cache) {
      RequestCacheInstance.cache = {}
    }

    Cookies.remove(STORAGE_KEYS.selectedMerchant)
  },
  SET_TIME_ZONE: (state, timezone) => {
    state.timezone = timezone
  },
  SET_COLLECT_PHONE_NUMBER_DIALOG_VISIBLE: (state, visible) => {
    state.collectPhoneNumberDialogVisible = visible
  },
  SET_FINISHED_COLLECT_PHONE_NUMBER: (state, status) => {
    state.finishedCollectPhoneNumber = status
  },
}

const actions = {
  updateSelectedMerchant({ commit }, merchantId) {
    commit('SET_SELECTED_MERCHANT', merchantId)

    // TODO: This is the ad-hoc solution to reload the page with the new merchant selected. We should refactor this.
    if (isEmpty(router.currentRoute.query)) {
      window.location.reload()
    } else {
      // Remove query parameters from current URL and reload
      const currentPath = router.currentRoute.path
      const newUrl = `#${currentPath}`

      window.history.pushState({}, '', newUrl)
      window.location.reload()
    }
  },
  async getMerchantUserProfile({ dispatch, commit, getters, state }) {
    if (state.data) {
      return state.data
    }

    try {
      commit('SET_STATUS', { key: 'fetching', value: true })

      dispatch('setCollectPhoneNumberDialogVisible', false)

      const { data } = await MerchantUserApi.getMerchantUserProfile()

      commit('SET_ONBOARDING_ID', data.onboardingId)
      commit('SET_TIME_ZONE', data.timeZone)

      locator.chatbot.setChatMetaFields({
        merchant_authenticated_status: true,
        merchant_email: data.email || '',
        user_id: data.userId || '',
        user_email: data.email || '',
        user_name: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
      })

      if (!data?.phoneNumber && !state.finishedCollectPhoneNumber) {
        dispatch('setCollectPhoneNumberDialogVisible', true)
      } else if (data?.phoneNumber) {
        commit('SET_FINISHED_COLLECT_PHONE_NUMBER', true)
        cacheFinishedCollectPhoneNumber(true)
      }

      if (
        !getters.selectedMerchant &&
        data &&
        data.stores &&
        data.stores.length > 0
      ) {
        const merchantId = localStorage.getItem('selectedMerchant')
          ? localStorage.getItem('selectedMerchant')
          : data.stores[0].merchantId
        commit('SET_SELECTED_MERCHANT', merchantId)

        try {
          const {
            data: merchantProfile,
          } = await MerchantProfileApi.getProfile()
          const currentLanguage = store.getters['translations/currentLang']

          locator.tracking.identify(merchantId, {
            merchant_id: merchantProfile?.merchantId || merchantId,
            user_id: data?.userId || merchantProfile?.merchantId || '',
            brand_name: merchantProfile?.merchantName || '',
            merchant_tier: merchantProfile?.tier?.tier || '',
            country_code: merchantProfile?.country || '',
            language: currentLanguage,
            device_type: getDeviceType(),
            operating_system: getOperatingSystem(),
            email: data.email || '',
            phone_number: data.phoneNumber || '',
          })
        } catch (e) {
          locator.tracking.identify(merchantId, { merchant_id: merchantId })
        }

        // Feature flag from BE to enable AdTech module on FE
        const res = await getAdtechFeatureFlag()

        store.dispatch('ads/setFeatureFlags', res)
        if (res?.isAdtechEnabled) {
          data.permissions.push({
            permissionPath: '^/ads$',
            permissionName: 'Access Adtech',
            permissionNameAr: 'Access Adtech',
            code: 'access_adtech',
            isVisible: true,
          })
        }
      }

      // Convert to list
      data.roles = data.role?.split(',') || []

      commit('SET_STATUS', { key: 'fetching', value: false })
      commit('SET_MERCHANT_USER_DATA', data)

      return data
    } catch (error) {
      commit('SET_MERCHANT_ERROR', error)

      console.error('Error fetching user profile:', error)
    }
  },
  setCollectPhoneNumberDialogVisible({ commit }, visible) {
    commit('SET_COLLECT_PHONE_NUMBER_DIALOG_VISIBLE', visible)
  },
  setFinishedCollectPhoneNumber({ commit }, status) {
    commit('SET_FINISHED_COLLECT_PHONE_NUMBER', status)

    cacheFinishedCollectPhoneNumber(status)
  },
  closeCollectPhoneNumberDialog({ dispatch }) {
    dispatch('setCollectPhoneNumberDialogVisible', false)

    dispatch('setFinishedCollectPhoneNumber', true)
  },
  addMerchantUserPhoneNumber(_, dataToBeSent) {
    return MerchantUserApi.addMerchantUserPhoneNumber(dataToBeSent)
  },
  verifyCollectPhoneByOtp(_, dataToBeSent) {
    return MerchantUserApi.verifyCollectPhoneByOtp(dataToBeSent)
  },
  resendOtpCollectPhone(_, dataToBeSent) {
    return MerchantUserApi.resendOtpCollectPhone(dataToBeSent)
  },
  clearMerchantUserData({ commit }) {
    commit('CLEAR_MERCHANT_USER_DATA')
  },
}

export default { namespaced: true, state, getters, mutations, actions }
